// Helper to validate and track missing fields per onboarding step

// Check if a field has a value
const hasFieldValue = (user, field) => {
	if (!user) return false;
	const value = user[field];
	// For boolean fields, check if they're explicitly set (even if false)
	if (field === 'faqAcknowledged') {
		return value !== null && value !== undefined;
	}
	// For other fields, check if they have a truthy value
	return value && value !== '' && value !== null && value !== undefined;
};

// Field definitions for each step
const stepFieldDefinitions = {
	personal: {
		fields: [
			{ key: 'firstName', label: 'First Name', required: true },
			{ key: 'lastName', label: 'Last Name', required: true },
			{ key: 'email', label: 'Email', required: true },
			{ key: 'phone', label: 'Phone', required: true },
			{ key: 'designations', label: 'Designations', required: true },
			{ key: 'licenseNumber', label: 'License Number', required: true },
			{ key: 'certifications', label: 'Certifications', required: true }
		]
	},
	address: {
		fields: [
			{ key: 'officeAddress', label: 'Office Address', required: true },
			{ key: 'homeAddress', label: 'Home Address', required: true }
		]
	},
	broker: {
		fields: [
			{ key: 'brokerName', label: 'Broker Name', required: true },
			{ key: 'brokerLicense', label: 'Broker License', required: true }
		]
	},
	photos: {
		// Special handling for photos - uses imagesCounts
		requiresImages: true,
		imageRequirements: {
			headshots: { min: 1, label: 'Headshot' },
			logos: { min: 1, label: 'Personal Logo' },
			brokerLogos: { min: 1, label: 'Broker Logo' }
		}
	},
	branding: {
		fields: [
			{ key: 'tagline', label: 'Tagline', required: true },
			{ key: 'brandingColors', label: 'Branding Colors', required: true },
			{ key: 'hashtags', label: 'Hashtags', required: true },
			{ key: 'keywords', label: 'Keywords', required: true },
			{ key: 'specialties', label: 'Specialties', required: true },
			{ key: 'nicheMarkets', label: 'Niche Markets', required: true },
			{ key: 'uniqueness', label: 'What Makes You Unique', required: true },
			{ key: 'serviceAreas', label: 'Service Areas', required: true }
		]
	},
	'personal-details': {
		fields: [
			{ key: 'hobbies', label: 'Hobbies', required: true },
			{ key: 'travelDestinations', label: 'Travel Destinations', required: true },
			{ key: 'personalValues', label: 'Personal Values', required: true },
			{ key: 'personalStory', label: 'Personal Story', required: true },
			{ key: 'volunteerWork', label: 'Volunteer Work', required: true },
			{ key: 'otherDetails', label: 'Other Details', required: true }
		]
	},
	social: {
		fields: [
			{ key: 'instagramLink', label: 'Instagram Link', required: true },
			{ key: 'facebookLink', label: 'Facebook Link', required: true },
			{ key: 'linkedinLink', label: 'LinkedIn Link', required: true },
			{ key: 'socialMediaCredentials', label: 'Social Media Credentials', required: true },
			{ key: 'websiteUrl', label: 'Website URL', required: true },
			{ key: 'googleBusinessUrl', label: 'Google Business URL', required: true },
			{ key: 'websitePlatform', label: 'Website Platform', required: true },
			{ key: 'websiteCredentials', label: 'Website Credentials', required: true },
			{ key: 'crmName', label: 'CRM Software', required: true },
			{ key: 'crmUsername', label: 'CRM Username', required: true },
			{ key: 'crmPassword', label: 'CRM Password', required: true }
		]
	},
	scheduling: {
		fields: [
			{ key: 'preferredMeetingTimes', label: 'Preferred Meeting Times', required: true },
			{ key: 'additionalNotes', label: 'Additional Notes', required: true },
			{ key: 'faqAcknowledged', label: 'FAQ Acknowledged', required: true }
		]
	}
};

// Validate a single step
export const validateStep = (stepId, user, imagesCounts = {}) => {
	const stepDef = stepFieldDefinitions[stepId];
	if (!stepDef) return null;

	const result = {
		stepId,
		completedFields: [],
		missingFields: [],
		totalFields: 0,
		completionPercentage: 0,
		isComplete: false
	};

	// Handle photo step specially
	if (stepDef.requiresImages) {
		const reqs = stepDef.imageRequirements;
		result.totalFields = Object.keys(reqs).length;
		
		for (const [imageType, config] of Object.entries(reqs)) {
			const count = imagesCounts[imageType] || 0;
			if (count >= config.min) {
				result.completedFields.push({
					key: imageType,
					label: config.label,
					value: `${count} uploaded`
				});
			} else {
				result.missingFields.push({
					key: imageType,
					label: config.label,
					required: config.min,
					current: count
				});
			}
		}
	} else {
		// Handle regular fields
		result.totalFields = stepDef.fields.length;
		
		for (const field of stepDef.fields) {
			if (hasFieldValue(user, field.key)) {
				result.completedFields.push({
					key: field.key,
					label: field.label,
					value: user[field.key]
				});
			} else {
				result.missingFields.push({
					key: field.key,
					label: field.label,
					required: field.required
				});
			}
		}
	}

	result.completionPercentage = result.totalFields > 0 
		? Math.round((result.completedFields.length / result.totalFields) * 100)
		: 0;
	result.isComplete = result.missingFields.length === 0;

	return result;
};

// Validate all steps
export const validateAllSteps = (steps, user, imagesCounts = {}) => {
	const results = {};
	let totalComplete = 0;
	let totalFields = 0;
	let completedFields = 0;

	for (const step of steps) {
		const validation = validateStep(step.id, user, imagesCounts);
		if (validation) {
			results[step.id] = {
				...validation,
				title: step.title,
				icon: step.icon
			};
			
			if (validation.isComplete) totalComplete++;
			totalFields += validation.totalFields;
			completedFields += validation.completedFields.length;
		}
	}

	return {
		stepValidations: results,
		totalSteps: steps.length,
		completedSteps: totalComplete,
		totalFields,
		completedFields,
		overallPercentage: totalFields > 0 
			? Math.round((completedFields / totalFields) * 100)
			: 0,
		isComplete: totalComplete === steps.length
	};
};

// Get a summary of missing fields across all steps
export const getMissingFieldsSummary = (validationResults) => {
	const summary = [];
	
	for (const [stepId, validation] of Object.entries(validationResults.stepValidations)) {
		if (validation.missingFields.length > 0) {
			summary.push({
				stepId,
				title: validation.title,
				missingCount: validation.missingFields.length,
				missingFields: validation.missingFields
			});
		}
	}
	
	return summary;
};

// Get next incomplete step
export const getNextIncompleteStep = (steps, validationResults) => {
	for (let i = 0; i < steps.length; i++) {
		const step = steps[i];
		const validation = validationResults.stepValidations[step.id];
		if (validation && !validation.isComplete) {
			return { step, index: i };
		}
	}
	return null;
};